import React, { useState } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import {
    Layout,
    LAYOUT_DOCUMENTS_1_3,
    LAYOUT_DOCUMENTS_FULL,
} from '@europrocurement/l2d-modules/modules/Dossiers/constants/layouts';
import {
    useDossierDocuments,
    useDossierDocumentsFiles,
} from '@europrocurement/l2d-modules/modules/Dossiers/hooks';
import useFileChecker from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useFileChecker';
import { useSnackbar } from 'notistack';
import DossierDocumentsValidationHeader from '../DossierDocumentsValidationHeader/DossierDocumentsValidationHeader';
import DossierDocumentsValidationList from '../DossierDocumentsValidationList/DossierDocumentsValidationList';
import DocumentViewer from '../../DocumentViewer/DocumentViewer';

export type DossierDocumentsValidationProps = {
    currentLayout: Layout;
    onLayoutChange: (layout: Layout) => void;
};

const DossierDocumentsValidation: React.FunctionComponent<DossierDocumentsValidationProps> =
    function (props) {
        const { currentLayout, onLayoutChange } = props;
        const [listDisplayed, setListDisplayed] = useState<boolean>(true);
        const { updateFichier } = useDossierDocumentsFiles();
        const { getFichierContent } = useDossierDocuments();
        const { displayFilesCheckerForm } = useFileChecker();
        const { enqueueSnackbar } = useSnackbar();
        const { palette } = useTheme();

        const wrapperSx = {
            overflow: 'hidden',
            backgroundColor: palette.background.paper,
            borderRadius: '12px',
            borderColor: palette.grey[200],
            borderWidth: '1px',
            borderStyle: 'solid',
            boxShadow: '0px 2px 2.5px 0px rgba(193, 202, 215, 0.25)',
        };

        const handleChangeDocumentType = (fichierId: number, newTypeDocId: number) => {
            updateFichier(fichierId as number, { typeDocDossier: newTypeDocId });
        };

        const onDocumentStatusChange = (fichierId: number, statusId: number) => {
            updateFichier(fichierId as number, { status: statusId });
        };

        const navigationBtn = (
            <Button
                sx={{
                    display: 'flex',
                    gap: '0 8px',
                    alignItems: 'center',
                    color: palette.secondary.main,
                }}
                onClick={() => setListDisplayed((v) => !v)}
            >
                <FaOptionIcon
                    sx={{ lineHeight: 0, display: 'flex', alignItems: 'center', fontSize: '14px' }}
                    icon={
                        listDisplayed
                            ? ['fasl', 'left-from-bracket']
                            : ['fasl', 'right-from-bracket']
                    }
                />
                <Typography
                    sx={{ fontSize: '14px' }}
                    variant="caption"
                >
                    {listDisplayed ? 'Masquer les documents' : 'Afficher les documents'}
                </Typography>
            </Button>
        );

        const handleDeliassage = async (fichierId: number) => {
            const { files } = await updateFichier(fichierId as number, {
                typageAuto: true,
            });

            if (files) {
                const filteredFiles = files.filter((f) => f.id !== fichierId);
                if (filteredFiles.length > 0) {
                    // files checker modal
                    displayFilesCheckerForm([{ createdFiles: filteredFiles }], getFichierContent);
                } else {
                    enqueueSnackbar('Nouveaux documents non détectés', {
                        variant: 'info',
                        autoHideDuration: 2000,
                    });
                }
            }
        };

        const returnDocumentBasedOnLayout = () => {
            switch (currentLayout) {
                case LAYOUT_DOCUMENTS_FULL:
                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                overflow: 'hidden',
                                width: '100%',
                            }}
                        >
                            <DossierDocumentsValidationList
                                displayList={listDisplayed}
                                currentLayout={currentLayout}
                            />
                            <Box sx={{ width: '70%', overflowY: 'hidden', maxHeight: '75vh' }}>
                                <DocumentViewer />
                            </Box>
                        </Box>
                    );

                case LAYOUT_DOCUMENTS_1_3:
                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                overflow: 'hidden',
                                width: '100%',
                            }}
                        >
                            <DossierDocumentsValidationList
                                displayList={listDisplayed}
                                currentLayout={currentLayout}
                            />
                            <Box
                                sx={{
                                    width: `${listDisplayed ? '55%' : '100%'}`,
                                    overflowY: 'hidden',
                                    maxHeight: '75vh',
                                }}
                            >
                                <DocumentViewer />
                            </Box>
                        </Box>
                    );

                default:
                    return (
                        <Box
                            sx={{
                                position: 'relative',
                                minHeight: '90vh',
                            }}
                        >
                            <DossierDocumentsValidationList displayList={listDisplayed} />
                            <DocumentViewer />
                        </Box>
                    );
            }
        };

        return (
            <Box sx={wrapperSx}>
                <DossierDocumentsValidationHeader
                    currentLayout={currentLayout}
                    onLayoutChange={onLayoutChange}
                    onDocumentTypeChange={handleChangeDocumentType}
                    onDocumentStatusChange={onDocumentStatusChange}
                    onDocumentDeliassage={handleDeliassage}
                    navigationBtn={navigationBtn}
                />
                {returnDocumentBasedOnLayout()}
            </Box>
        );
    };

export default DossierDocumentsValidation;
